import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { isAddedAssetTheme, youtubeWatchUrl, type AddedAssetTheme } from 'fym-shared/src/music24/addedAssets';
import type { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { isAddedAsset, sourceOfAsset, videoSizeLabel } from '../../addedAssets/addedAssetFacts';
import { addedAssetsMessages } from '../../addedAssets/addedAssetsMessages';
import { addAssetRequestOf } from '../../addedAssets/addAssetRequestOf';
import { openAddAssetDialog, type AddAssetTab } from '../../addedAssets/addAssetDialogStore';
import { isAddedUploadRunning, useAddedUpload } from '../../addedAssets/addedUploadStore';
import { useOpenMusicVideoEditor } from '../../addedAssets/useOpenMusicVideoEditor';
import type { KitSlotView } from './kitSlots';

/**
 * The "Add yours" side of one Kit slot (design music-video a1, a5–a7): the dialog of the slot, the
 * editor of the music video, the remove confirm, the picture and the badge of an added version
 * ("Linked", "Your file · 4K"; a linked video opens on YouTube), and the line of an upload on the
 * way.
 */
export function useKitSlotAdded(input: { accountId: number; item: CatalogItem; view: KitSlotView }) {
  const { accountId, item, view } = input;
  const intl = useIntl();
  const themeId: AddedAssetTheme | null = isAddedAssetTheme(view.slot.id) ? view.slot.id : null;
  const asset = view.readyAsset;
  const source = sourceOfAsset(asset);

  const openAdd = useCallback(
    (tab?: AddAssetTab) => {
      if (themeId !== null) {
        openAddAssetDialog(addAssetRequestOf(accountId, item, themeId, tab));
      }
    },
    [accountId, item, themeId]
  );
  const openEditorOfSong = useOpenMusicVideoEditor(accountId);
  const openEditor = useCallback(
    (cut?: string | null) => openEditorOfSong(item.uuid, cut),
    [item.uuid, openEditorOfSong]
  );

  const [removeUuid, setRemoveUuid] = useState<string | null>(null);
  const askRemove = useCallback(() => setRemoveUuid(asset?.uuid ?? null), [asset]);
  const closeRemove = useCallback(() => setRemoveUuid(null), []);

  const upload = useAddedUpload(item.uuid, view.slot.id);
  const uploadLine = useMemo(() => {
    if (!isAddedUploadRunning(upload) || upload === null) {
      return null;
    }
    const percent = upload.sizeBytes > 0 ? Math.floor((upload.sentBytes / upload.sizeBytes) * 100) : 0;
    return intl.formatMessage(addedAssetsMessages.uploadingLine, { percent });
  }, [intl, upload]);

  const thumb = useMemo(() => {
    if (view.state !== 'made' || asset === null || !isAddedAsset(asset)) {
      return { pictureUrl: undefined, badge: null, onOpen: undefined };
    }
    if (source === 'youtube' && asset.youtube !== undefined && asset.youtube !== null) {
      const url = youtubeWatchUrl(asset.youtube.videoId);
      return {
        pictureUrl: asset.youtube.thumbnailUrl,
        badge: intl.formatMessage(addedAssetsMessages.linkedBadge),
        onOpen: () => {
          window.open(url, '_blank', 'noopener');
        },
      };
    }
    const size = videoSizeLabel(asset.height);
    return {
      pictureUrl: undefined,
      badge:
        size === null
          ? intl.formatMessage(addedAssetsMessages.yourFile)
          : intl.formatMessage(addedAssetsMessages.yourFileSize, { size }),
      onOpen: undefined,
    };
  }, [asset, intl, source, view.state]);

  return {
    themeId,
    source,
    canRemove: view.state === 'made' && isAddedAsset(asset),
    openAdd,
    openEditor,
    removeUuid,
    askRemove,
    closeRemove,
    uploadLine,
    thumb,
  };
}
